import React, { useState } from 'react';
import { Bell, Plus, Check, Search, Settings } from 'lucide-react';
import './Showcase.css';

type Tab = 'feed' | 'schedule' | 'profile' | 'social';

const tabs: { id: Tab; label: string }[] = [
  { id: 'feed', label: 'Activity Feed' },
  { id: 'schedule', label: 'Schedule' },
  { id: 'profile', label: 'Player Profile' },
  { id: 'social', label: 'Social' },
];

const Showcase: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('feed');

  return (
    <section className="showcase" id="community">
      <div className="showcase__container">
        <div className="section-header">
          <div className="section-badge">In the App</div>
          <h2>Built for regular<br /><span className="text-gradient">pickleball coordination</span></h2>
          <p className="section-subtitle">The website now reflects the actual app surfaces: Home, Schedule, Courts, Social, and Profile.</p>
        </div>

        <div className="showcase__tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`showcase__tab ${activeTab === tab.id ? 'showcase__tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="showcase__content">
          {activeTab === 'feed' && <FeedPanel />}
          {activeTab === 'schedule' && <SchedulePanel />}
          {activeTab === 'profile' && <ProfilePanel />}
          {activeTab === 'social' && <SocialPanel />}
        </div>
      </div>
    </section>
  );
};

const CheckMark = () => <Check size={18} color="#6BA314" />;

const FeedPanel: React.FC = () => (
  <div className="showcase__panel">
    <div className="showcase__desc">
      <h3>Friend Activity Feed</h3>
      <p>Your Home screen shows recent check-ins from friends, active join intents, and notification counts for friend requests and session updates.</p>
      <ul className="showcase__list">
        <li><CheckMark /> Check-in activity from friends from the last 24 hours</li>
        <li><CheckMark /> Arrival windows when someone joins a check-in</li>
        <li><CheckMark /> Quick navigation to court and player details</li>
      </ul>
    </div>
    <div className="showcase__visual">
      <div className="mini-phone">
        <div className="mini-phone__screen">
          <div className="mini-phone__header">
            <span className="mini-phone__title">Home</span>
            <div className="mini-phone__icons">
              <Bell size={20} color="#6BA314" />
              <Plus size={20} color="#6BA314" />
            </div>
          </div>
          <div className="mini-feed">
            {[
              { name: 'Sarah Martinez', time: '5m ago', court: 'Riverside Center', duration: 'playing until 3:30 PM', color: '#B8FF3C' },
              { name: 'Mike Davidson', time: '12m ago', court: 'Riverside Center', duration: 'playing until 4:00 PM', color: '#0088FF' },
              { name: 'Lisa Kim', time: '25m ago', court: 'Valley Sports', duration: 'playing until 5:00 PM', color: '#FF9900' },
            ].map((item, i) => (
              <div className="mini-feed__card" key={i}>
                <div className="mini-feed__avatar-wrap">
                  <div className="mini-feed__avatar" style={{ background: `linear-gradient(135deg, ${item.color}, ${item.color}88)` }} />
                  <div className="mini-feed__location-dot" />
                </div>
                <div className="mini-feed__body">
                  <div className="mini-feed__row">
                    <span className="mini-feed__name">{item.name}</span>
                    <span className="mini-feed__time">{item.time}</span>
                  </div>
                  <div className="mini-feed__action">
                    <span className="mini-feed__pin">At</span> <strong>{item.court}</strong>, {item.duration}
                  </div>
                </div>
                <button className="mini-feed__join">Join</button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const SchedulePanel: React.FC = () => (
  <div className="showcase__panel">
    <div className="showcase__desc">
      <h3>Plan Sessions with Friends</h3>
      <p>Use the Schedule tab to view friend sessions, filter by court or group, and create new sessions for a selected day.</p>
      <ul className="showcase__list">
        <li><CheckMark /> Create sessions from the schedule calendar</li>
        <li><CheckMark /> Invite players and track RSVP status</li>
        <li><CheckMark /> Filter by courts, friends, or groups</li>
        <li><CheckMark /> View session details and updates</li>
      </ul>
    </div>
    <div className="showcase__visual">
      <div className="mini-phone">
        <div className="mini-phone__screen">
          <div className="mini-phone__header">
            <span className="mini-phone__title">Schedule</span>
            <Plus size={16} color="#6BA314" />
          </div>
          <div className="mock-calendar">
            <div className="mock-calendar__days">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                <span key={i} className={i === 3 ? 'mock-calendar__day--active' : ''}>{d}</span>
              ))}
            </div>
          </div>
          <div className="mock-sessions">
            {[
              { title: 'Morning Drill Session', court: 'Riverside Center', time: '9:00 AM', players: '4/6', color: '#B8FF3C' },
              { title: 'Open Play with Sarah', court: 'Central Park Courts', time: '2:00 PM', players: '3/4', color: '#0088FF' },
              { title: 'Tournament Practice', court: 'Valley Sports Complex', time: '6:00 PM', players: '5/8', color: '#FF9900' },
            ].map((s, i) => (
              <div className="mock-session" key={i}>
                <div className="mock-session__color" style={{ background: s.color }} />
                <div className="mock-session__info">
                  <div className="mock-session__title">{s.title}</div>
                  <div className="mock-session__meta">{s.court} - {s.time}</div>
                </div>
                <span className="mock-session__players">{s.players}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const ProfilePanel: React.FC = () => (
  <div className="showcase__panel">
    <div className="showcase__desc">
      <h3>Your Player Profile</h3>
      <p>Profiles include basic identity details, home court, current check-in status, self-rating, and visible friend/follower activity.</p>
      <ul className="showcase__list">
        <li><CheckMark /> Set a self-rating from 1.0 to 5.5</li>
        <li><CheckMark /> Show rating information when available on the account</li>
        <li><CheckMark /> View check-in stats and activity history</li>
        <li><CheckMark /> Manage profile, location, and appearance settings</li>
      </ul>
    </div>
    <div className="showcase__visual">
      <div className="mini-phone">
        <div className="mini-phone__screen">
          <div className="mini-phone__header">
            <span className="mini-phone__title">Profile</span>
            <div className="mini-phone__icons"><Settings size={18} color="#6BA314" /></div>
          </div>
          <div className="mock-profile">
            <div className="mock-profile__avatar" />
            <div className="mock-profile__name">Alex Johnson</div>
            <div className="mock-profile__loc">Portland, OR</div>
            <div className="mock-profile__pills">
              <span className="mock-pill mock-pill--gender">Male</span>
              <span className="mock-pill mock-pill--court">Riverside Center</span>
            </div>
            <div className="mock-profile__status">
              <span className="mock-status-badge">Playing at Riverside - 2h left</span>
              <button className="mock-checkout-btn">Check Out</button>
            </div>
            <div className="mock-ratings">
              <div className="mock-rating">
                <div className="mock-rating__header">Self Rating</div>
                <div className="mock-rating__value">4.0</div>
                <div className="mock-rating__sub">Intermediate+</div>
              </div>
              <div className="mock-rating">
                <div className="mock-rating__header">Rating</div>
                <div className="mock-rating__value">--</div>
                <div className="mock-rating__sub">When available</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const SocialPanel: React.FC = () => (
  <div className="showcase__panel">
    <div className="showcase__desc">
      <h3>Your Pickleball Network</h3>
      <p>The Social tab brings together friends, groups, player search, profiles, and messaging so players can coordinate beyond a single check-in.</p>
      <ul className="showcase__list">
        <li><CheckMark /> Send and manage friend requests</li>
        <li><CheckMark /> Create friend groups for easier filtering</li>
        <li><CheckMark /> See who is currently checked in</li>
        <li><CheckMark /> Open one-to-one and group conversations</li>
      </ul>
    </div>
    <div className="showcase__visual">
      <div className="mini-phone">
        <div className="mini-phone__screen">
          <div className="mini-phone__header">
            <span className="mini-phone__title">Social</span>
            <Plus size={18} color="#6BA314" />
          </div>
          <div className="mock-search-bar">
            <Search size={14} color="#9CA3AF" />
            <span>Search players...</span>
          </div>
          <div className="mock-groups">
            <span className="mock-group mock-group--active">All (12)</span>
            <span className="mock-group">Weekday Crew</span>
            <span className="mock-group">Tournament</span>
            <span className="mock-group mock-group--add">+</span>
          </div>
          <div className="mock-friends">
            {[
              { name: 'Sarah Martinez', loc: 'At Riverside Center', rating: '4.5', color: '#B8FF3C', active: true },
              { name: 'Mike Davidson', loc: 'Seattle, WA', rating: '3.5', color: '#0088FF', active: false },
              { name: 'Lisa Kim', loc: 'At Valley Sports', rating: '4.0', color: '#FF9900', active: true },
              { name: 'Jordan Taylor', loc: 'Vancouver, WA', rating: '3.0', color: '#9CA3AF', active: false },
            ].map((f, i) => (
              <div className="mock-friend" key={i}>
                <div className="mock-friend__avatar-wrap">
                  <div className="mock-friend__avatar" style={{ background: `linear-gradient(135deg, ${f.color}, ${f.color}88)` }} />
                  {f.active && <div className="mock-friend__active-dot" />}
                </div>
                <div className="mock-friend__info">
                  <div className="mock-friend__name">{f.name}</div>
                  <div className={`mock-friend__meta ${f.active ? 'mock-friend__meta--active' : ''}`}>{f.loc}</div>
                </div>
                <span className="mock-skill-badge">{f.rating}</span>
                <span className="mock-friend__chevron">&gt;</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default Showcase;
